import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Stack,
  Typography,
  IconButton,
  Box,
  Container,
  Divider,
  Link,
  Chip,
} from '@mui/material';
import Iconify from '../Iconify';
import MsciDetails from '@/sections/msi/MsciDetail';
import { fCurrency } from '../../utils/formatNumber';
import TicketDetails from './TicketDetails';

function EventCheckoutDetails({
  resume: { ticketsResumeDetail, commissions },
  booking: {
    orderInfo: {
      installments,
      boletia_percentage_total: totalServiceFee,
      payment_method_commission_total: totalProcessorFee,
      boletia_amount_total: totalTransactionFee,
      tickets_total: subtotalTickets,
      total,
      discount,
      discount_coupon: promotion,
      ticket_insurance: { price: ticketInsurance },
      currency,
    },
  },
  onClose = () => {},
  captionButton = '',
  hideAction = false,
  hideHeader = false,
  hideTotal = false,
  onRemovePromotion,
  onAddPromotion,
  onRemoveTicketInsurance,
}) {
  const { t } = useTranslation();

  const { preferences: { fee_breakdown = 't' } = {} } = useSelector(
    (state) => state.event || {},
  );
  return (
    <>
      <Container>
        {!hideHeader && (
          <Stack
            direction="row"
            sx={{
              width: '100%',
              height: '56px',
              alignItems: 'center',
            }}
          >
            <Box sx={{ minWidth: 80 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  lineHeight: '56px',
                }}
              >
                {t('checkout.global.yourTickets')}
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }}></Box>
            {!hideAction && (
              <>
                {captionButton && (
                  <Link
                    href="#"
                    variant="caption"
                    sx={{ textDecoration: 'underline' }}
                    onClick={onClose}
                  >
                    {captionButton}
                  </Link>
                )}
                {!captionButton && (
                  <IconButton onClick={onClose}>
                    <Iconify
                      width={14}
                      height={14}
                      icon={'material-symbols:keyboard-arrow-down'}
                    />
                  </IconButton>
                )}
              </>
            )}
          </Stack>
        )}
        <Stack spacing={1} sx={{ mb: 1, mt: hideHeader ? 2 : 0 }}>
          {ticketsResumeDetail.map((item) => (
            <TicketDetails
              key={item.id}
              title={item.name}
              subtitle={item.zone}
              iconify={item.color ? 'material-symbols:circle' : null}
              iconColor={item.color}
              sx={{
                alignItems: 'center',
              }}
              nodeEnd={
                <Typography variant="caption" sx={{ fontWeight: 400 }}>
                  {`${fCurrency(item.total)} ${currency}`}
                </Typography>
              }
            />
          ))}
          {promotion && discount > 0 && (
            <Stack direction="row" sx={{ width: '100%', alignItems: 'center' }}>
              <Box sx={{ minWidth: 80 }}>
                <Typography variant="subtitle2">
                  <Chip
                    label={promotion}
                    size="small"
                    icon={<Iconify width={14} height={14} icon={'mdi:tag'} />}
                    color="secondary"
                    onDelete={onRemovePromotion ? onRemovePromotion : null}
                  />
                </Typography>
              </Box>
              <Box sx={{ flexGrow: 1 }}></Box>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: (theme) => theme.palette.secondary.main,
                  }}
                >
                  {`- ${fCurrency(discount)} ${currency}`}
                </Typography>
              </Box>
            </Stack>
          )}
        </Stack>
      </Container>
      <Stack
        sx={{ mt: 2, mb: 2 }}
        direction="row"
        justifyContent="center"
        alignItems="center"
      >
        <Divider sx={{ width: '90%' }} />
      </Stack>
      <Container>
        <Stack
          direction="row"
          sx={{ width: '100%', mt: 1, alignItems: 'center' }}
        >
          <Box sx={{ minWidth: 80 }}>
            <Typography variant="body3">
              {t('checkout.global.subtotal')}
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }}></Box>
          <Box>
            <Typography variant="body2">
              {`${fCurrency(subtotalTickets - discount)} ${currency}`}
            </Typography>
          </Box>
        </Stack>
        {!promotion && !!onAddPromotion && discount <= 0 && (
          <Stack direction="row" sx={{ width: '100%', alignItems: 'center' }}>
            <Box sx={{ minWidth: 80 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: (theme) => theme.palette.secondary.main,
                }}
              >
                {t('checkout.coupons.haveCoupon')}
                <Iconify width={14} height={14} icon={'mdi:tag'} />
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }}></Box>
            <Box>
              <Link
                onClick={onAddPromotion}
                variant="body2"
                sx={{ textDecoration: 'underline' }}
              >
                {t('checkout.buttons.apply')}
              </Link>
            </Box>
          </Stack>
        )}
        {promotion && discount <= 0 && (
          <Stack direction="row" sx={{ width: '100%', alignItems: 'center' }}>
            <Box sx={{ minWidth: 80 }}>
              <Typography variant="subtitle2">
                <Chip
                  label={promotion}
                  size="small"
                  icon={<Iconify width={14} height={14} icon={'mdi:tag'} />}
                  color="secondary"
                  onDelete={onRemovePromotion ? onRemovePromotion : null}
                />
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }}></Box>
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: (theme) => theme.palette.secondary.main,
                }}
              >
                {t('checkout.global.noPromo')}
              </Typography>
            </Box>
          </Stack>
        )}
        {commissions ? (
          <>
            <Stack direction="row" sx={{ width: '100%', alignItems: 'center' }}>
              <Box sx={{ minWidth: 80 }}>
                <Typography variant="body3">
                  {t('checkout.global.charge')}
                </Typography>
              </Box>
              <Box sx={{ flexGrow: 1 }}></Box>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    lineHeight: '36px',
                  }}
                >
                  {`${fCurrency(commissions.boletia_commission)} ${currency}`}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" sx={{ width: '100%', alignItems: 'center' }}>
              <Box sx={{ minWidth: 80 }}>
                <Typography variant="body3">
                  {t('checkout.global.paymentProcessor')}
                </Typography>
              </Box>
              <Box sx={{ flexGrow: 1 }}></Box>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    lineHeight: '36px',
                  }}
                >
                  {`${fCurrency(
                    commissions.payment_method_commission,
                  )} ${currency}`}
                </Typography>
              </Box>
            </Stack>
          </>
        ) : (
          <>
            {' '}
            {fee_breakdown === 't' && (
              <>
                <Stack
                  direction="row"
                  sx={{ width: '100%', alignItems: 'center' }}
                >
                  <Box sx={{ minWidth: 80 }}>
                    <Typography variant="body3">
                      {t('checkout.global.charge')}
                    </Typography>
                  </Box>
                  <Box sx={{ flexGrow: 1 }}></Box>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        lineHeight: '36px',
                      }}
                    >
                      {`${fCurrency(totalServiceFee)} ${currency}`}
                    </Typography>
                  </Box>
                </Stack>
                <Stack
                  direction="row"
                  sx={{ width: '100%', alignItems: 'center' }}
                >
                  <Box sx={{ minWidth: 80 }}>
                    <Typography variant="body3">
                      {t('checkout.global.ticketFee')}
                    </Typography>
                  </Box>
                  <Box sx={{ flexGrow: 1 }}></Box>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        lineHeight: '36px',
                      }}
                    >
                      {`${fCurrency(totalTransactionFee)} ${currency}`}
                    </Typography>
                  </Box>
                </Stack>
                <Stack
                  direction="row"
                  sx={{ width: '100%', alignItems: 'center' }}
                >
                  <Box sx={{ minWidth: 80 }}>
                    <Typography variant="body3">
                      {t('checkout.global.paymentMethodCharge')}
                    </Typography>
                  </Box>
                  <Box sx={{ flexGrow: 1 }}></Box>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        lineHeight: '36px',
                      }}
                    >
                      {`${fCurrency(totalProcessorFee)} ${currency}`}
                    </Typography>
                  </Box>
                </Stack>
              </>
            )}
          </>
        )}
        {ticketInsurance > 0 && (
          <>
            <Stack
              sx={{ mt: 2, mb: 2 }}
              direction="row"
              justifyContent="center"
              alignItems="center"
            >
              <Divider sx={{ width: '100%' }} />
            </Stack>
            <Stack
              direction="row"
              sx={{ width: '100%', alignItems: 'center', marginBottom: '4px' }}
            >
              <Box sx={{ minWidth: 80 }}>
                <Typography variant="body3" component={'div'}>
                  Protección de tu compra
                </Typography>
                {onRemoveTicketInsurance && (
                  <Typography
                    variant="caption2"
                    onClick={onRemoveTicketInsurance}
                    component={'div'}
                    sx={{
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    }}
                  >
                    Quitar{' '}
                    <Iconify
                      width={12}
                      height={12}
                      icon={'simple-line-icons:close'}
                    />
                  </Typography>
                )}
              </Box>
              <Box sx={{ flexGrow: 1 }}></Box>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    lineHeight: '36px',
                  }}
                >
                  {`${fCurrency(ticketInsurance)} ${currency}`}
                </Typography>
              </Box>
            </Stack>
          </>
        )}
        <Stack
          sx={{ mt: 2, mb: 2 }}
          direction="row"
          justifyContent="center"
          alignItems="center"
        >
          <Divider sx={{ width: '100%' }} />
        </Stack>
        {!hideTotal && (
          <Stack>
            <Stack direction="row" sx={{ width: '100%', alignItems: 'center' }}>
              <Box sx={{ minWidth: 80 }}>
                <Typography variant="h5">
                  {t('checkout.global.total')}
                </Typography>
              </Box>
              <Box sx={{ flexGrow: 1 }}></Box>
              <Box>
                <Typography variant="h5">
                  {`${fCurrency(total)} ${currency}`}
                </Typography>
              </Box>
            </Stack>

            {installments?.payment_per_month > 0 && (
              <MsciDetails installments={installments} currency={currency} />
            )}
          </Stack>
        )}
      </Container>
    </>
  );
}

EventCheckoutDetails.propTypes = {
  resume: PropTypes.object.isRequired,
  onClose: PropTypes.func,
  onRemovePromotion: PropTypes.func,
  onAddPromotion: PropTypes.func,
  EventCheckoutDetails: PropTypes.string,
  hideAction: PropTypes.bool,
};

export default EventCheckoutDetails;
